/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { NewCoreIconProps } from '@atlaskit/icon/types';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import { type ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common/types';
import { createEditorToolbarActions } from '@atlaskit/native-embeds-common/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core/types/features/main';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import { getInitialThemeParam } from '@atlassian/native-embeds-core/theme';
import {
	type Experience,
	type ExperienceManifest,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

import { ARTIFACTS_EXPERIENCE_MANIFEST_ID } from './manifest-id';

export { ARTIFACTS_EXPERIENCE_MANIFEST_ID } from './manifest-id';

const artifactsIcons = [ShapesIcon];

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * Claims Artifacts app URLs in either of two forms:
 *
 *   `<host>/artifacts/<artifactId>/*`
 *   `<host>/apps/<installationId>/<artifactId>/*?smartlink=artifact`
 *
 * Only the path and query components participate in matching. The URL origin
 * and fragment are intentionally ignored.
 */
const ARTIFACT_URL_PATTERNS = [
	new RegExp(`/artifacts/${UUID}(?:[/?#].*)?$`),
	new RegExp(`/apps/${UUID}/${UUID}/[^?#]*\\?(?:[^#]*&)?smartlink=artifact`),
];

const isArtifactsUrl = (url: string): boolean =>
	// Gated behind the Forge UI artifact Confluence integration flag: the Artifacts
	// experience only claims URLs when the flag is enabled.
	fg('platform_forge_ui_artifact_confluence_integration') &&
	ARTIFACT_URL_PATTERNS.some((pattern) => pattern.test(url));

/**
 * Experience manifest for an Artifacts embed.
 * Registered via `getNativeEmbedFacade().setup({ experiences: [...] })` in
 * `@atlassian/native-embeds-editor-extension`.
 */
export const ArtifactsExperienceManifest: {
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly resolveViaLinkingPlatform: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		readonly editorToolbarHandlers: {};
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		readonly headerControls: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly useResolvedTitleIcon: true;
		readonly waitForContentReady: true;
	};
} = {
	id: ARTIFACTS_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {
		// No Artifacts-specific child commands yet.
	},
	isUrlMatch: isArtifactsUrl,
	isActive: (): true => true,
	// Route through the Smart Card / linking-platform resolver so the embed renders
	// via `<Card appearance="embed">` (EmbedFrameWrapper uses `useBasicIframe: false`).
	// Requires the Object Resolver Service to resolve Artifacts share URLs and return a
	// `preview`. Set to `false` to render a basic iframe directly from `transformUrl`.
	resolveViaLinkingPlatform: true,
	supportedFeatures: new Set<NativeEmbedFeature>([...allNativeEmbedCoreFeatures]),
	transformUrl: (url: string, isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		if (!isFullScreen) {
			urlObj.searchParams.set('hideBorder', 'true');
		}
		// Identifies the native embed view to the Artifacts app.
		urlObj.searchParams.set('nativeEmbedView', 'true');

		const initialTheme = getInitialThemeParam();
		if (initialTheme) {
			urlObj.searchParams.set('themeState', initialTheme);
		}

		return urlObj.toString();
	},
	uiConfig: {
		// Header edit button intentionally disabled for now. Re-add an `editButton`
		// entry here (using `swapArtifactsRoute`) to restore the inline edit flow.
		headerControls: {},
		editorToolbarActions: (): ManifestEditorToolbarActions<any> =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				],
				moreItems: [
					BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
			}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={artifactsIcons}
				testId={props.testId}
			/>
		),
		titleIcon: ShapesIcon,
		// Use the linking-platform-resolved icon (the artifact's own icon) in the
		// header, falling back to ShapesIcon until it resolves.
		useResolvedTitleIcon: true,
		appearance: 'overlay',
		waitForContentReady: true,
	},
} as const satisfies ExperienceManifest;
